import React, { useMemo, useState, useEffect } from "react";
import DynamicRadarChart from "./dynamicradar";
import ZoomModal from "./heatmap/zoomModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MENU_ICON, ZOOM_ICON } from "../utills/image-constant";

const SubDimensionCharts = ({ responseBody = [] }) => {
  const [activeZoom, setActiveZoom] = useState({
    id: null,
    title: "",
    chartOptions: null,
    seriesData: null,
  });

  const chartData = useMemo(() => {
    return (responseBody || []).map((dimension) => {
      const subs =
        dimension?.subDimensions ||
        dimension?.subDimensionRadarChartResponseList ||
        dimension?.dimensionResponseList ||
        [];

      const categories = subs.map(
        (sub) => sub.subDimensionName || sub.name || "N/A"
      );
      const importanceScores = subs.map((sub) =>
        Number(
          sub.importantAverageValue ?? sub.importantValue ?? sub.importance ?? 0
        )
      );
      const currentScores = subs.map((sub) =>
        Number(sub.currentAverageValue ?? sub.currentValue ?? sub.current ?? 0)
      );
      const futureScores = subs.map((sub) =>
        Number(sub.futureAverageValue ?? sub.futureValue ?? sub.future ?? 0)
      );

      const chartOptions = {
        chart: { type: "radar" },
        xaxis: { categories },
        stroke: { width: 2 },
        fill: { opacity: 0.3 },
        markers: { size: 4 },
        legend: { position: "bottom" },
        title: { text: dimension.dimensionName || "Radar Chart" },
      };

      const seriesData = [
        { name: "Current Score", data: currentScores },
        { name: "Future Score", data: futureScores },
        { name: "Importance Score", data: importanceScores },
      ];

      return {
        rawDimension: dimension,
        dimensionName: dimension.dimensionName || "Unnamed",
        chartData: {
          labels: categories,
          datasets: [
            {
              label: "Current Score",
              data: currentScores,
              borderColor: "rgba(82,127,51,1)",
              backgroundColor: "rgba(82,127,51,0.2)",
              pointBackgroundColor: "rgba(82,127,51,1)",
            },
            {
              label: "Future Score",
              data: futureScores,
              borderColor: "rgba(254,190,0,1)",
              backgroundColor: "rgba(254,190,0,0.2)",
              pointBackgroundColor: "rgba(254,190,0,1)",
            },
            {
              label: "Importance Score",
              data: importanceScores,
              borderColor: "rgba(223,17,29,1)",
              backgroundColor: "rgba(223,17,29,0.2)",
              pointBackgroundColor: "rgba(223,17,29,1)",
            },
          ],
        },
        chartOptions,
        seriesData,
      };
    });
  }, [responseBody]);

  const exportToExcel = (item) => {
    const rows = [["Subdimension", "Current", "Future", "Importance"]];
    const subs =
      item?.rawDimension?.subDimensions ||
      item?.rawDimension?.subDimensionRadarChartResponseList ||
      item?.rawDimension?.dimensionResponseList ||
      [];

    subs.forEach((s) => {
      rows.push([
        s.subDimensionName || s.name || "",
        s.currentAverageValue ?? s.currentValue ?? 0,
        s.futureAverageValue ?? s.futureValue ?? 0,
        s.importantAverageValue ?? s.importantValue ?? 0,
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, item.dimensionName);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `${item.dimensionName}.xlsx`);
  };

  const exportAsPDF = async (chartId, title = "RadarChart") => {
    const el = document.getElementById(chartId);
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2 });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height * w) / canvas.width;
    pdf.text(title, 10, 10);
    pdf.addImage(img, "PNG", 10, 20, w - 20, h);
    pdf.save(`${title.replace(/\s+/g, "_")}.pdf`);
  };

  const exportAsImage = async (chartId, title = "RadarChart") => {
    const el = document.getElementById(chartId);
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2 });
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const openZoom = (modalId, title, chartOptions, seriesData) => {
    setActiveZoom({ id: modalId, title, chartOptions, seriesData });
  };

  // useEffect(() => {
  //   if (!activeZoom || !activeZoom.title) return;
  //   const modalEl = document.getElementById("globalZoomModal");
  //   if (modalEl && window.bootstrap) {
  //     const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
  //     bsModal.show();
  //   }
  // }, [activeZoom]);
useEffect(() => {
    if (activeZoom?.title) {
      const modalEl = document.getElementById("globalZoomModal");
 
      // Ensure modal exists before trying to open it
      if (modalEl && window.bootstrap) {
        const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
        bsModal.show();
 
        // After modal closes, clear activeZoom state to reset for next modal
        modalEl.addEventListener(
          "hidden.bs.modal",
          () => {
            setActiveZoom({ id: null, title: "", chartOptions: null, seriesData: null });
          },
          { once: true }
        );
      }
    }
  }, [activeZoom]); // Runs every time activeZoom
  return (
    <div>
      {chartData.length > 0 ? (
        chartData.map((item, index) => {
          const chartId = `radarChart-${index}`;
          const modalId = `zoomModal-${index}`;

          return (
            <div key={index} className="card mb-3 shadow-sm">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
                <h6 className="m-0">{item.dimensionName}</h6>
                <div className="d-flex align-items-center">
                  <button
                    className="border-0 bg-transparent me-2"
                    title="Zoom View"
                    onClick={() =>
                      openZoom(
                        modalId,
                        item.dimensionName,
                        item.chartOptions,
                        item.seriesData
                      )
                    }
                  >
                    <img src={ZOOM_ICON} alt="zoom" />
                  </button>
                  <div className="dropdown">
                    <button
                      className="icon-btn bg-transparent"
                      data-bs-toggle="dropdown"
                    >
                      <img src={MENU_ICON} alt="menu" />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => exportToExcel(item)}
                        >
                          Download Excel
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            exportAsPDF(chartId, item.dimensionName)
                          }
                        >
                          Export PDF
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            exportAsImage(chartId, item.dimensionName)
                          }
                        >
                          Download Image
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div id={chartId} className="p-2">
                {item?.chartData ? (
                  <DynamicRadarChart data={item} />
                ) : (
                  <p className="text-center text-muted">No radar chart data</p>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-center mt-3">No subdimension data available</p>
      )}

      {/* Global Zoom Modal */}
      <ZoomModal
        id="globalZoomModal"
        title={activeZoom.title || ""}
        chartOptions={activeZoom.chartOptions}
        seriesData={activeZoom.seriesData}
        type="radar"
        height={550}
      />
    </div>
  );
};

export default SubDimensionCharts;
